import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Heading, VStack, Input, Button, FormControl, FormLabel, Spinner, useToast, Center,
  Table, Thead, Tbody, Tr, Th, Td, IconButton, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, Flex, Divider,
  Text, HStack, Badge, useColorModeValue, Card, CardBody, Alert, AlertIcon, Container,
  InputGroup, InputLeftElement, SimpleGrid, Tooltip, InputLeftAddon
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, WarningIcon, InfoIcon, SearchIcon } from '@chakra-ui/icons';
import { FiRefreshCw, FiUpload, FiPlus, FiList, FiGrid } from 'react-icons/fi';

const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_VERCEL_API_URL
  : process.env.REACT_APP_LOCAL_API_URL;

const CategoriesPage = () => {
  // States
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [name, setName] = useState('');
  const [imageRes, setImageRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedFileName, setSelectedFileName] = useState('');
  // State to hold the current category to delete
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'

  // Theme colors
  const bgMain = useColorModeValue('gray.50', 'gray.800');
  const bgCard = "#fff8e1";
  const bgInput = useColorModeValue('white', 'gray.800');
  const themeColor = "#4bedffff";
  const themeTextColor = "black";

  // Modals
  const { isOpen: isFormModalOpen, onOpen: onFormModalOpen, onClose: onFormModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const toast = useToast();

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}api/categories`);
      const data = await response.json();
      setCategories(data);
      setFilteredCategories(data);
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao carregar categorias',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Load data on component mount
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCategories();
  }, []);

  // Filter categories when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCategories(categories);
    } else {
      const lowercasedSearch = searchTerm.toLowerCase();
      const filtered = categories.filter(
        (category) => category.name.toLowerCase().includes(lowercasedSearch)
      );
      setFilteredCategories(filtered);
    }
  }, [categories, searchTerm]);

  // Handle file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFileName(file.name);
    }
    
    if (file.type === 'image/svg+xml') {
      const reader = new FileReader();
      reader.onload = () => {
        const svgContent = reader.result;
        convertSvgToXml(svgContent);
      };
      reader.readAsText(file);
    } else if (file.type === 'text/xml') {
      const reader = new FileReader();
      reader.onload = () => {
        setImageRes(reader.result);
      };
      reader.readAsText(file);
    } else {
      toast({
        title: 'Arquivo Inválido',
        description: 'Por favor, faça upload de um arquivo SVG ou XML',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Convert SVG to XML
  const convertSvgToXml = (svgContent) => {
    setImageRes(svgContent);
    toast({
      title: 'Sucesso',
      description: 'SVG convertido para XML com sucesso',
      status: 'success',
      duration: 5000,
      isClosable: true,
    });
  };

  // Add new category
  const handleSubmit = async () => {
    if (!name || !imageRes) {
      toast({
        title: 'Erro',
        description: 'Por favor, forneça o nome da categoria e o arquivo SVG/XML',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }
  
    setLoading(true);
  
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/categories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name, imageRes }),
      });
  
      const data = await response.json();
  
      if (response.ok) {
        setCategories([...categories, data]);
        setName('');
        setImageRes(null);
        setSelectedFileName('');
        
        // Reset file input if it exists
        const fileInput = document.getElementById("imageRes");
        if (fileInput) fileInput.value = "";

        toast({
          title: 'Sucesso',
          description: 'Categoria adicionada com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        
        onFormModalClose();
      } else {
        toast({
          title: 'Erro',
          description: data.error || 'Falha ao adicionar categoria',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao adicionar categoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Delete category
  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const response = await fetch(`${API_BASE_URL}api/categories/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setCategories(categories.filter(category => category._id !== id));
        toast({
          title: 'Sucesso',
          description: 'Categoria excluída com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onDeleteModalClose();
      } else {
        toast({
          title: 'Erro',
          description: 'Falha ao excluir categoria',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao excluir categoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Set up form for editing
  const handleEdit = (category) => {
    setEditingCategory(category);
    setName(category.name);
    setImageRes(category.imageRes);
    onFormModalOpen();
  };

  // Update category
  const handleUpdate = async () => {
    if (!editingCategory) return;

    setLoading(true);

    try {
      const token = localStorage.getItem('userToken_TOVIAPPS');
      const updatedCategory = {
        name,
        imageRes: imageRes || editingCategory.imageRes,
      };
      const response = await fetch(`${API_BASE_URL}api/categories/${editingCategory._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updatedCategory),
      });

      const data = await response.json();

      if (response.ok) {
        setCategories(categories.map(cat => cat._id === editingCategory._id ? data : cat));
        setEditingCategory(null);
        setName('');
        setImageRes(null);
        setSelectedFileName('');
        
        // Reset file input if it exists
        const fileInput = document.getElementById("imageRes");
        if (fileInput) fileInput.value = "";

        toast({
          title: 'Sucesso',
          description: 'Categoria atualizada com sucesso',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        onEditModalClose();
        onFormModalClose();
      } else {
        toast({
          title: 'Erro',
          description: 'Falha ao atualizar categoria',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Falha ao atualizar categoria',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Start new category form
  const handleAddNew = () => {
    setEditingCategory(null);
    setName('');
    setImageRes(null);
    setSelectedFileName('');
    onFormModalOpen();
  };

  // Main component render
  return (
    <Container maxW="container.xl" p={6}>
      {/* Header */}
      <Card mb={6} shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap">
            <HStack>
              <Heading as="h1" fontSize="2xl" fontWeight="bold" color="black">
                Categorias
              </Heading>
              <Badge bg={themeColor} color={themeTextColor} borderRadius="full" px={3} py={1}>
                {categories.length} categoria{categories.length !== 1 ? 's' : ''}
              </Badge>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                borderColor={themeColor}
                color="black"
                onClick={fetchCategories}
                isLoading={loading}
              >
                Atualizar
              </Button>
              
              <Button
                leftIcon={<FiPlus />}
                bg={themeColor}
                color={themeTextColor}
                onClick={handleAddNew}
                _hover={{ bg: "#e5c100" }}
              >
                Nova Categoria
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Search and Filter Bar */}
      <Card mb={6} shadow="sm" borderRadius="lg" bg={bgCard}>
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
            <HStack flexGrow={1} maxW={{ base: "100%", md: "320px" }}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Buscar categorias..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </InputGroup>
            </HStack>
            
            <HStack spacing={3}>
              <HStack spacing={2}>
                <IconButton
                  icon={<FiList />}
                  aria-label="Lista"
                  bg={viewMode === 'list' ? themeColor : 'gray.100'}
                  color={viewMode === 'list' ? themeTextColor : 'gray.500'}
                  onClick={() => setViewMode('list')}
                  borderRadius="md"
                />
                <IconButton
                  icon={<FiGrid />}
                  aria-label="Grid"
                  bg={viewMode === 'grid' ? themeColor : 'gray.100'}
                  color={viewMode === 'grid' ? themeTextColor : 'gray.500'}
                  onClick={() => setViewMode('grid')}
                  borderRadius="md"
                />
              </HStack>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Content Area */}
      {loading && categories.length === 0 ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Center py={10}>
              <VStack spacing={4}>
                <Spinner size="xl" thickness="4px" color={themeColor} />
                <Text color="black">Carregando categorias...</Text>
              </VStack>
            </Center>
          </CardBody>
        </Card>
      ) : filteredCategories.length === 0 ? (
        <Card shadow="md" borderRadius="lg" bg={bgCard}>
          <CardBody>
            <Alert status="info" borderRadius="md" variant="subtle">
              <AlertIcon />
              {searchTerm 
                ? 'Nenhuma categoria encontrada com os termos de busca aplicados' 
                : 'Nenhuma categoria encontrada. Adicione sua primeira categoria usando o botão acima.'}
            </Alert>
          </CardBody>
        </Card>
      ) : viewMode === 'list' ? (
        // List View
        <Card shadow="md" borderRadius="lg" overflow="hidden" bg={bgCard}>
          <Box overflowX="auto">
            <Table colorScheme="yellow" size="md">
              <Thead bg={themeColor}>
                <Tr>
                  <Th color={themeTextColor}>Nome</Th>
                  <Th color={themeTextColor}>XML/SVG</Th>
                  <Th width="130px" textAlign="center" color={themeTextColor}>Ações</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredCategories.map((category) => (
                  <Tr key={category._id} _hover={{ bg: 'gray.50' }} transition="all 0.2s">
                    <Td fontWeight="medium" color="black">{category.name}</Td>
                    <Td>
                      <Flex align="center">
                        <Box
                          w="40px"
                          h="40px"
                          mr={3}
                          bg="white"
                          borderRadius="md"
                          p={1}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          overflow="hidden"
                          borderColor="gray.200"
                          borderWidth="1px"
                          dangerouslySetInnerHTML={{
                            __html: category.imageRes || '<svg viewBox="0 0 24 24"><rect width="24" height="24" fill="#ccc"/></svg>'
                          }}
                        />
                        <Text noOfLines={1} maxW="250px" fontSize="sm" fontFamily="mono" color="gray.500">
                          {category.imageRes ? `${category.imageRes.substring(0, 30)}...` : 'N/A'}
                        </Text>
                      </Flex>
                    </Td>
                    <Td>
                      <HStack spacing={2} justify="center">
                        <Tooltip label="Editar Categoria" placement="top">
                          <IconButton
                            icon={<EditIcon />}
                            aria-label="Editar categoria"
                            bg={themeColor}
                            color={themeTextColor}
                            size="sm"
                            onClick={() => handleEdit(category)}
                            _hover={{ bg: "#e5c100" }}
                          />
                        </Tooltip>
                        <Tooltip label="Excluir Categoria" placement="top">
                          <IconButton
                            icon={<DeleteIcon />}
                            aria-label="Excluir categoria"
                            bg={themeColor}
                            color={themeTextColor}
                            size="sm"
                            onClick={() => {
                              // Find the full category object
                              const categoryObj = categories.find(c => c._id === category._id);
                              setCategoryToDelete(categoryObj);
                              onDeleteModalOpen();
                            }}
                            _hover={{ bg: "#e5c100" }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Card>
      ) : (
        // Grid View
        <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={6}>
          {filteredCategories.map((category) => (
            <Card 
              key={category._id} 
              shadow="md" 
              borderRadius="lg" 
              overflow="hidden"
              transition="all 0.3s"
              _hover={{ transform: 'translateY(-5px)', shadow: 'lg' }}
              bg={bgCard}
            >
              <Box bg={themeColor} h="8px" w="100%" />
              <CardBody p={4}>
                <Flex direction="column" h="100%">
                  <Heading size="md" mb={2} noOfLines={1} color="black">
                    {category.name}
                  </Heading>
                  <Text fontSize="sm" color="gray.500" mb={3} noOfLines={2} fontFamily="mono">
                    <Box
                      w="50px"
                      h="50px"
                      mx="auto"
                      my={2}
                      bg="white"
                      borderRadius="md"
                      p={1}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      borderColor="gray.200"
                      borderWidth="1px"
                      dangerouslySetInnerHTML={{
                        __html: category.imageRes || '<svg viewBox="0 0 24 24"><rect width="24" height="24" fill="#ccc"/></svg>'
                      }}
                    />
                    {category.imageRes ? category.imageRes.substring(0, 30) + '...' : 'N/A'}
                  </Text>
                  <Flex mt="auto" justify="flex-end" pt={3}>
                    <HStack spacing={2}>
                      <IconButton
                        icon={<EditIcon />}
                        aria-label="Editar categoria"
                        bg={themeColor}
                        color={themeTextColor}
                        size="sm"
                        onClick={() => handleEdit(category)}
                        _hover={{ bg: "#e5c100" }}
                      />
                      <IconButton
                        icon={<DeleteIcon />}
                        aria-label="Excluir categoria"
                        bg={themeColor}
                        color={themeTextColor}
                        size="sm"
                        onClick={() => {
                        // Find the full category object
                        const categoryObj = categories.find(c => c._id === category._id);
                          setCategoryToDelete(categoryObj);
                            onDeleteModalOpen();
                          }}
                        _hover={{ bg: "#e5c100" }}
                      />
                    </HStack>
                  </Flex>
                </Flex>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      )}

      {/* Add/Edit Form Modal */}
      <Modal isOpen={isFormModalOpen} onClose={onFormModalClose} size="md">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            {editingCategory ? "Editar Categoria" : "Nova Categoria"}
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <VStack spacing={5} align="stretch">
              <FormControl id="name" isRequired>
                <FormLabel fontWeight="medium" color="black">Nome da Categoria</FormLabel>
                <Input
                  placeholder="Digite o nome da categoria"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  focusBorderColor={themeColor}
                  bg="white"
                />
              </FormControl>

              <FormControl id="imageResField" isRequired>
                <FormLabel fontWeight="medium" color="black">Ícone da Categoria (SVG ou XML)</FormLabel>

                {/* Hidden file input */}
                <Input 
                  type="file" 
                  accept=".svg,.xml" 
                  onChange={handleFileChange} 
                  id="imageRes" 
                  style={{ display: 'none' }} 
                />

                <HStack spacing={3} mt={1}>
                  {/* Custom button to trigger file selection */}
                  <Button 
                    bg={themeColor}
                    color={themeTextColor}
                    as="label" 
                    htmlFor="imageRes"
                    leftIcon={<FiUpload />}
                    size="md"
                    _hover={{ bg: "#e5c100" }}
                  >
                    {selectedFileName ? "Alterar Arquivo" : "Selecionar Arquivo"}
                  </Button>
                  
                  {/* Show selected file status */}
                  {selectedFileName ? (
                    <Badge colorScheme="green" p={2} borderRadius="md">
                      {selectedFileName}
                    </Badge>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      {editingCategory && imageRes ? "Arquivo atual mantido" : "Nenhum arquivo selecionado"}
                    </Text>
                  )}
                </HStack>
                
                {/* SVG Preview */}
                {(imageRes || (editingCategory && editingCategory.imageRes)) && (
                  <Box 
                    mt={4} 
                    p={3} 
                    bg="white" 
                    borderRadius="md" 
                    border="1px solid" 
                    borderColor="gray.200"
                  >
                    <Text fontWeight="medium" mb={2} fontSize="sm" color="black">Pré-visualização:</Text>
                    <Flex justify="center" align="center" direction="column">
                      <Box
                        w="80px"
                        h="80px"
                        bg="white"
                        borderRadius="md"
                        p={2}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        overflow="hidden"
                        boxShadow="sm"
                        dangerouslySetInnerHTML={{
                          __html: imageRes || (editingCategory ? editingCategory.imageRes : '')
                        }}
                      />
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        Visualização aproximada do ícone no aplicativo
                      </Text>
                    </Flex>
                  </Box>
                )}
                
                {/* File instructions */}
                <Text fontSize="xs" color="gray.500" mt={2}>
                  Apenas arquivos SVG ou XML são aceitos. O ícone será exibido no aplicativo móvel.
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onFormModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={editingCategory ? onEditModalOpen : handleSubmit}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              {editingCategory ? 'Atualizar' : 'Adicionar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Edit Confirmation Modal */}
      <Modal isOpen={isEditModalOpen} onClose={onEditModalClose} size="sm">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            Confirmar Atualização
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            <Flex alignItems="center">
              <InfoIcon color={themeColor} boxSize={5} mr={3} />
              <Text color="black">
                Tem certeza que deseja atualizar a categoria <strong>{editingCategory?.name}</strong>?
              </Text>
            </Flex>
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onEditModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={handleUpdate}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Confirmar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      
      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteModalOpen} onClose={onDeleteModalClose} size="sm">
        <ModalOverlay backdropFilter="blur(5px)" />
        <ModalContent borderRadius="lg" bg={bgCard}>
          <Box bg={themeColor} h="8px" w="100%" />
          <ModalHeader color="black">
            Confirmar Exclusão
          </ModalHeader>
          <ModalCloseButton color="black" />
          <ModalBody py={6}>
            {categoryToDelete && (
              <VStack spacing={4} align="stretch">
                <Flex alignItems="center">
                  <WarningIcon color={themeColor} boxSize={5} mr={3} />
                  <Text color="black">
                    Tem certeza que deseja excluir a categoria <strong>{categoryToDelete.name}</strong>? Esta ação não poderá ser desfeita e pode afetar as subcategorias associadas.
                  </Text>
                </Flex>
                
                <Box 
                  p={3} 
                  bg="white" 
                  borderRadius="md" 
                  border="1px solid" 
                  borderColor="gray.200"
                >
                  <Flex direction="column" align="center">
                    <Text fontWeight="medium" mb={2} fontSize="sm" color="black">Pré-visualização do ícone:</Text>
                    <Box
                      w="80px"
                      h="80px"
                      bg="white"
                      borderRadius="md"
                      p={2}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      overflow="hidden"
                      boxShadow="sm"
                      dangerouslySetInnerHTML={{
                        __html: categoryToDelete.imageRes || '<svg viewBox="0 0 24 24"><rect width="24" height="24" fill="#ccc"/></svg>'
                      }}
                    />
                  </Flex>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter bg="gray.50" borderBottomRadius="lg">
            <Button variant="ghost" mr={3} onClick={onDeleteModalClose}>
              Cancelar
            </Button>
            <Button
              bg={themeColor}
              color={themeTextColor}
              onClick={() => handleDelete(categoryToDelete?._id)}
              isLoading={loading}
              _hover={{ bg: "#e5c100" }}
            >
              Excluir
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default CategoriesPage;